import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBoxOpen,
  FaEye,
  FaTruck,
  FaMapMarkerAlt,
  FaCreditCard,
  FaReceipt,
  FaChevronRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaExternalLinkAlt,
} from "react-icons/fa";
import api from "../api";

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const user = JSON.parse(
    localStorage.getItem("user") || "null"
  );

  const token = user?.access;

  // =========================================================
  // COLORS
  // =========================================================

  const cream = "#F5EFE3";
  const creamLight = "#FBF8F1";
  const teal = "#0F766E";
  const tealDark = "#115E59";
  const brown = "#8B5E3C";
  const border = "#E7DCC9";

  // =========================================================
  // STATUS COLORS
  // =========================================================

  const statusColors = {
    unpaid: "bg-[#F0E8DD] text-[#6B5A4C]",
    paid: "bg-[#E2F0ED] text-[#46736C]",
    pending: "bg-[#F5EEDB] text-[#806B35]",
    processing: "bg-[#E3EEEC] text-[#385F59]",
    shipped: "bg-[#E8E5F0] text-[#5E5870]",
    delivered: "bg-[#E2F0ED] text-[#46736C]",
    completed: "bg-[#E2F0ED] text-[#46736C]",
    cancelled: "bg-red-100 text-red-700",
  };

  // =========================================================
  // STATUS STEPS
  // =========================================================

  const statusSteps = [
    {
      key: "paid",
      label: "Order placed",
    },
    {
      key: "processing",
      label: "Processing",
    },
    {
      key: "shipped",
      label: "Shipped",
    },
    {
      key: "delivered",
      label: "Delivered",
    },
  ];

  const statusRank = {
    unpaid: 0,
    pending: 0,
    paid: 1,
    processing: 2,
    shipped: 3,
    delivered: 4,
    completed: 4,
  };

  // =========================================================
  // FETCH ORDERS
  // =========================================================

  useEffect(() => {
    const fetchOrders = async () => {
      if (!token) {
        setError(
          "Please log in to view your orders."
        );

        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          "/api/orders/",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = res.data;

        console.log("Orders:", data);

        if (Array.isArray(data)) {
          setOrders(data);
        } else if (Array.isArray(data.results)) {
          setOrders(data.results);
        } else {
          setOrders([]);
        }
      } catch (err) {
        console.error(
          "Failed to fetch orders:",
          err
        );

        if (err.response) {
          console.error(
            "Status:",
            err.response.status
          );

          console.error(
            "Response:",
            err.response.data
          );
        }

        setError(
          "Unable to load your orders. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  // =========================================================
  // HELPERS
  // =========================================================

  const getOrderStatus = (order) => {
    return (
      order?.status
        ?.toString()
        .trim()
        .toLowerCase() || "pending"
    );
  };

  const formatStatus = (status) => {
    if (!status) {
      return "Unknown";
    }

    return (
      status.charAt(0).toUpperCase() +
      status.slice(1)
    );
  };

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString();
  };

  const formatShortDate = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMoney = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "$0.00";
    }

    return `$${number.toFixed(2)}`;
  };

  // =========================================================
  // ORDER ITEMS
  //
  // Supports several common API field names so the UI
  // remains flexible with your backend.
  // =========================================================

  const getOrderItems = (order) => {
    if (Array.isArray(order?.items)) {
      return order.items;
    }

    if (Array.isArray(order?.order_items)) {
      return order.order_items;
    }

    if (Array.isArray(order?.orderItems)) {
      return order.orderItems;
    }

    if (Array.isArray(order?.line_items)) {
      return order.line_items;
    }

    return [];
  };

  const getItemName = (item) => {
    return (
      item?.product?.name ||
      item?.product_name ||
      item?.name ||
      "Rug"
    );
  };

  const getItemImage = (item) => {
    return (
      item?.product?.sub_images?.[0]?.image ||
      item?.product?.image ||
      item?.image ||
      item?.product_image ||
      "/placeholder.jpg"
    );
  };

  const getItemQuantity = (item) => {
    const quantity = Number(
      item?.quantity ??
        item?.qty ??
        1
    );

    return Number.isFinite(quantity)
      ? quantity
      : 1;
  };

  const getItemSize = (item) => {
    const size =
      item?.size ||
      item?.product_size ||
      item?.size_name ||
      item?.size_label;

    if (!size) {
      return null;
    }

    if (typeof size === "string") {
      return size;
    }

    if (typeof size === "object") {
      if (
        size.length_cm !== undefined &&
        size.width_cm !== undefined
      ) {
        return `${size.length_cm} × ${size.width_cm} cm`;
      }

      if (
        size.length_ft !== undefined &&
        size.width_ft !== undefined
      ) {
        return `${size.length_ft} × ${size.width_ft} ft`;
      }

      return (
        size.name ||
        size.label ||
        size.size ||
        null
      );
    }

    return null;
  };

  const getItemUnitPrice = (item) => {
    const price =
      item?.unit_price ??
      item?.price ??
      item?.discount_price;

    const number = Number(price);

    if (!Number.isFinite(number)) {
      return null;
    }

    return number;
  };

  const getItemTotal = (item) => {
    const explicitTotal =
      item?.total_price ??
      item?.line_total ??
      item?.subtotal ??
      item?.total;

    if (
      explicitTotal !== null &&
      explicitTotal !== undefined
    ) {
      const number = Number(
        explicitTotal
      );

      if (Number.isFinite(number)) {
        return number;
      }
    }

    const unitPrice =
      getItemUnitPrice(item);

    if (unitPrice === null) {
      return null;
    }

    return (
      unitPrice *
      getItemQuantity(item)
    );
  };

  // =========================================================
  // ORDER SUMMARY FIELDS
  // =========================================================

  const getSubtotal = (order) => {
    return (
      order?.subtotal ??
      order?.sub_total ??
      order?.items_total ??
      order?.merchandise_total ??
      null
    );
  };

  const getShippingCost = (order) => {
    return (
      order?.shipping_cost ??
      order?.shipping_fee ??
      order?.shipping ??
      null
    );
  };

  const getTax = (order) => {
    return (
      order?.tax ??
      order?.tax_amount ??
      null
    );
  };

  const getDiscount = (order) => {
    return (
      order?.discount ??
      order?.discount_amount ??
      null
    );
  };

  const getPaymentMethod = (order) => {
    if (typeof order?.payment_method === "string") {
      return order.payment_method;
    }

    if (
      order?.payment_method &&
      typeof order.payment_method === "object"
    ) {
      return (
        order.payment_method.name ||
        order.payment_method.method ||
        order.payment_method.type ||
        null
      );
    }

    return (
      order?.payment?.method ||
      order?.payment?.type ||
      null
    );
  };

  const getShippingAddress = (order) => {
    const address =
      order?.shipping_address ||
      order?.shippingAddress ||
      order?.delivery_address ||
      order?.address;

    if (!address) {
      return null;
    }

    if (typeof address === "string") {
      return address;
    }

    if (typeof address === "object") {
      const parts = [
        address.name,
        address.full_name,
        address.address,
        address.address_line_1,
        address.address_line_2,
        address.city,
        address.state,
        address.province,
        address.postal_code,
        address.zip_code,
        address.country,
      ].filter(Boolean);

      return parts.join(", ");
    }

    return null;
  };

  const getTrackingNumber = (order) => {
    return (
      order?.tracking_number ||
      order?.trackingNumber ||
      order?.tracking?.number ||
      order?.shipping?.tracking_number ||
      null
    );
  };

  const getCarrier = (order) => {
    return (
      order?.carrier ||
      order?.shipping_carrier ||
      order?.tracking_carrier ||
      order?.tracking?.carrier ||
      order?.shipping?.carrier ||
      null
    );
  };

  const getTrackingUrl = (order) => {
    return (
      order?.tracking_url ||
      order?.trackingUrl ||
      order?.tracking?.url ||
      order?.shipping?.tracking_url ||
      null
    );
  };

  const getEstimatedDelivery = (order) => {
    return (
      order?.estimated_delivery ||
      order?.estimated_delivery_date ||
      order?.delivery_date ||
      order?.shipping?.estimated_delivery ||
      null
    );
  };

  // =========================================================
  // ITEM COUNT
  // =========================================================

  const getTotalItemCount = (order) => {
    const items = getOrderItems(order);

    if (items.length === 0) {
      return 0;
    }

    return items.reduce(
      (total, item) =>
        total + getItemQuantity(item),
      0
    );
  };

  // =========================================================
  // STATUS TIMELINE
  // =========================================================

  const renderTimeline = (order) => {
    const status =
      getOrderStatus(order);

    if (status === "cancelled") {
      return (
        <div className="mt-6">
          <div
            className="
              rounded-xl
              border
              border-red-200
              bg-red-50
              px-4
              py-3
            "
          >
            <div className="flex items-center gap-2">
              <FaClock className="text-red-500" />

              <span className="font-semibold text-red-700">
                Order cancelled
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (status === "unpaid") {
      return (
        <div className="mt-6">
          <div
            className="
              rounded-xl
              border
              border-[#E7DCC9]
              bg-[#FFF9F0]
              px-4
              py-3
            "
          >
            <div className="flex items-center gap-2">
              <FaClock className="text-[#806B35]" />

              <span className="font-medium text-[#806B35]">
                Payment is still pending
              </span>
            </div>
          </div>
        </div>
      );
    }

    const currentRank =
      statusRank[status] ?? 0;

    return (
      <div className="mt-6">
        <div className="flex items-start">
          {statusSteps.map(
            (step, index) => {
              const stepRank = index + 1;
              const completed =
                currentRank >= stepRank;

              const isLast =
                index ===
                statusSteps.length - 1;

              return (
                <div
                  key={step.key}
                  className="
                    flex-1
                    min-w-0
                  "
                >
                  <div className="flex items-center">

                    <div
                      className={`
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border-2
                        transition-all
                        duration-500

                        ${
                          completed
                            ? "border-[#0F766E] bg-[#0F766E] text-white"
                            : "border-[#D8CBB7] bg-[#FFFDF9] text-[#B8AA9D]"
                        }
                      `}
                    >
                      {completed ? (
                        <FaCheckCircle className="text-sm" />
                      ) : (
                        <span className="text-[10px]">
                          {index + 1}
                        </span>
                      )}
                    </div>

                    {!isLast && (
                      <div
                        className={`
                          h-[2px]
                          flex-1
                          mx-2
                          transition-all
                          duration-500

                          ${
                            currentRank >
                            stepRank
                              ? "bg-[#0F766E]"
                              : "bg-[#E1D7C8]"
                          }
                        `}
                      />
                    )}
                  </div>

                  <p
                    className={`
                      mt-2
                      pr-2
                      text-[9px]
                      sm:text-xs
                      font-medium

                      ${
                        completed
                          ? "text-[#115E59]"
                          : "text-[#8A8172]"
                      }
                    `}
                  >
                    {step.label}
                  </p>
                </div>
              );
            }
          )}
        </div>
      </div>
    );
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div
        className="
          min-h-screen
          flex
          justify-center
          items-center
          px-4
          bg-[#F6F0E6]
        "
      >
        <div className="text-center">
          <div
            className="
              mx-auto
              mb-5
              h-10
              w-10
              rounded-full
              border-4
              border-[#DCCFBE]
              border-t-[#0F766E]
              animate-spin
            "
          />

          <p
            className="
              text-base
              sm:text-lg
              text-[#76695D]
            "
          >
            Loading your orders...
          </p>
        </div>
      </div>
    );
  }

  // =========================================================
  // ERROR
  // =========================================================

  if (error) {
    return (
      <div
        className="
          min-h-screen
          bg-[#F6F0E6]
          py-10
          sm:py-12
        "
      >
        <div
          className="
            max-w-3xl
            mx-auto
            px-4
          "
        >
          <div
            className="
              bg-[#FFFDF9]
              rounded-2xl
              shadow-sm
              border
              border-[#E7DCC9]
              p-8
              sm:p-10
              text-center
            "
          >
            <FaBoxOpen
              className="
                mx-auto
                text-5xl
                text-[#B8AA9D]
                mb-5
              "
            />

            <h2
              className="
                text-2xl
                font-semibold
                mb-3
                text-[#332A24]
              "
            >
              Unable to load orders
            </h2>

            <p
              className="
                text-[#76695D]
                mb-6
              "
            >
              {error}
            </p>

            <button
              onClick={() =>
                window.location.reload()
              }
              className="
                bg-[#0F766E]
                text-white

                px-6
                py-3

                rounded-xl

                hover:bg-[#115E59]

                transition-all

                font-semibold
              "
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN
  // =========================================================

  return (
    <div
      className="
        min-h-screen
        bg-[#F6F0E6]
      "
    >
      <div
        className="
          max-w-6xl
          mx-auto

          px-3
          sm:px-4
          lg:px-6

          py-8
          sm:py-10
          lg:py-14
        "
      >

        {/* =====================================================
            PAGE HEADER
        ===================================================== */}

        <div
          className="
            flex
            flex-col
            sm:flex-row
            sm:items-end
            sm:justify-between
            gap-4
            mb-8
            sm:mb-10
          "
        >
          <div>
            <div
              className="
                flex
                items-center
                gap-3
                mb-2
              "
            >
              <div
                className="
                  flex
                  h-11
                  w-11
                  items-center
                  justify-center
                  rounded-xl
                  bg-[#E2F0ED]
                  text-[#0F766E]
                "
              >
                <FaBoxOpen />
              </div>

              <h1
                className="
                  text-2xl
                  sm:text-3xl
                  lg:text-4xl
                  font-bold
                  text-[#332A24]
                "
              >
                My Orders
              </h1>
            </div>

            <p
              className="
                text-sm
                sm:text-base
                text-[#76695D]
              "
            >
              View your purchases, delivery status,
              and order details.
            </p>
          </div>

          {orders.length > 0 && (
            <div
              className="
                inline-flex
                items-center
                gap-2
                self-start
                sm:self-auto

                rounded-full

                bg-[#FFFDF9]

                border
                border-[#E7DCC9]

                px-4
                py-2

                text-xs
                sm:text-sm

                font-medium

                text-[#76695D]
              "
            >
              <FaReceipt className="text-[#0F766E]" />

              {orders.length}{" "}
              {orders.length === 1
                ? "order"
                : "orders"}
            </div>
          )}
        </div>

        {/* =====================================================
            NO ORDERS
        ===================================================== */}

        {orders.length === 0 ? (
          <div
            className="
              bg-[#FFFDF9]

              rounded-2xl

              border
              border-[#E7DCC9]

              shadow-sm

              p-8
              sm:p-12

              text-center
            "
          >
            <div
              className="
                mx-auto

                flex
                h-20
                w-20

                items-center
                justify-center

                rounded-full

                bg-[#F0E8DD]

                text-[#B8AA9D]
              "
            >
              <FaBoxOpen className="text-4xl" />
            </div>

            <h2
              className="
                mt-6

                text-2xl
                sm:text-3xl

                font-semibold

                text-[#332A24]
              "
            >
              No orders yet
            </h2>

            <p
              className="
                mt-3

                max-w-md
                mx-auto

                text-sm
                sm:text-base

                leading-relaxed

                text-[#76695D]
              "
            >
              Once you purchase something
              from A1 Rugs, your order will
              appear here.
            </p>

            <button
              onClick={() =>
                navigate("/products/All")
              }
              className="
                mt-7

                inline-flex
                items-center
                gap-2

                rounded-xl

                bg-[#0F766E]

                px-6
                py-3

                text-sm

                font-semibold

                text-white

                shadow-sm

                transition-all

                hover:bg-[#115E59]
                hover:-translate-y-0.5
                hover:shadow-md
              "
            >
              Browse Rugs

              <FaChevronRight className="text-xs" />
            </button>
          </div>
        ) : (

          /* =====================================================
             ORDERS
          ===================================================== */

          <div className="space-y-6">

            {orders.map((order) => {
              const status =
                getOrderStatus(order);

              const statusClass =
                statusColors[status] ||
                "bg-[#F0E8DD] text-[#6B5A4C]";

              const items =
                getOrderItems(order);

              const itemCount =
                getTotalItemCount(order);

              const subtotal =
                getSubtotal(order);

              const shipping =
                getShippingCost(order);

              const tax =
                getTax(order);

              const discount =
                getDiscount(order);

              const paymentMethod =
                getPaymentMethod(order);

              const shippingAddress =
                getShippingAddress(order);

              const trackingNumber =
                getTrackingNumber(order);

              const carrier =
                getCarrier(order);

              const trackingUrl =
                getTrackingUrl(order);

              const estimatedDelivery =
                getEstimatedDelivery(order);

              return (
                <article
                  key={
                    order.order_number ||
                    order.id
                  }
                  className="
                    overflow-hidden

                    rounded-2xl

                    border
                    border-[#E7DCC9]

                    bg-[#FFFDF9]

                    shadow-sm

                    transition-all
                    duration-300

                    hover:shadow-lg
                  "
                >

                  {/* =================================================
                      ORDER HEADER
                  ================================================= */}

                  <div
                    className="
                      border-b
                      border-[#E7DCC9]

                      bg-[#FBF8F1]

                      px-4
                      sm:px-6

                      py-4
                      sm:py-5
                    "
                  >
                    <div
                      className="
                        flex
                        flex-col
                        lg:flex-row
                        lg:items-center
                        lg:justify-between

                        gap-4
                      "
                    >

                      {/* LEFT */}

                      <div
                        className="
                          flex
                          flex-wrap
                          items-center
                          gap-x-5
                          gap-y-2
                        "
                      >

                        <div>
                          <p
                            className="
                              text-[10px]
                              sm:text-xs

                              uppercase
                              tracking-[0.08em]

                              font-medium

                              text-[#8A8172]
                            "
                          >
                            Order number
                          </p>

                          <h2
                            className="
                              mt-0.5

                              text-base
                              sm:text-lg

                              font-bold

                              text-[#332A24]
                            "
                          >
                            #
                            {order.order_number ||
                              order.id}
                          </h2>
                        </div>

                        {order.created_at && (
                          <div>
                            <p
                              className="
                                text-[10px]
                                sm:text-xs

                                uppercase
                                tracking-[0.08em]

                                font-medium

                                text-[#8A8172]
                              "
                            >
                              Ordered
                            </p>

                            <p
                              className="
                                mt-0.5

                                text-xs
                                sm:text-sm

                                text-[#5F554D]
                              "
                            >
                              {formatShortDate(
                                order.created_at
                              )}
                            </p>
                          </div>
                        )}

                        {itemCount > 0 && (
                          <div>
                            <p
                              className="
                                text-[10px]
                                sm:text-xs

                                uppercase
                                tracking-[0.08em]

                                font-medium

                                text-[#8A8172]
                              "
                            >
                              Items
                            </p>

                            <p
                              className="
                                mt-0.5

                                text-xs
                                sm:text-sm

                                text-[#5F554D]
                              "
                            >
                              {itemCount}{" "}
                              {itemCount === 1
                                ? "item"
                                : "items"}
                            </p>
                          </div>
                        )}

                      </div>

                      {/* RIGHT */}

                      <div
                        className="
                          flex
                          flex-wrap
                          items-center
                          gap-3
                        "
                      >

                        {/* STATUS */}

                        <span
                          className={`
                            inline-flex
                            items-center
                            gap-2

                            rounded-full

                            px-3
                            sm:px-4

                            py-1.5
                            sm:py-2

                            text-[10px]
                            sm:text-xs

                            font-semibold

                            ${statusClass}
                          `}
                        >
                          <span
                            className="
                              h-1.5
                              w-1.5

                              rounded-full

                              bg-current
                            "
                          />

                          {formatStatus(status)}
                        </span>

                        {/* TOTAL */}

                        <div className="text-right">
                          <p
                            className="
                              text-[10px]
                              sm:text-xs

                              text-[#8A8172]
                            "
                          >
                            Order total
                          </p>

                          <p
                            className="
                              mt-0.5

                              text-lg
                              sm:text-xl

                              font-bold

                              text-[#0F766E]
                            "
                          >
                            {formatMoney(
                              order.total_amount
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                      ORDER BODY
                  ================================================= */}

                  <div
                    className="
                      px-4
                      sm:px-6

                      py-5
                      sm:py-6
                    "
                  >

                    {/* =================================================
                        ITEMS
                    ================================================= */}

                    {items.length > 0 && (
                      <div>
                        <div
                          className="
                            flex
                            items-center
                            justify-between

                            mb-4
                          "
                        >
                          <h3
                            className="
                              text-sm
                              sm:text-base

                              font-semibold

                              text-[#332A24]
                            "
                          >
                            Items in this order
                          </h3>

                          <span
                            className="
                              text-xs

                              text-[#8A8172]
                            "
                          >
                            {itemCount}{" "}
                            {itemCount === 1
                              ? "item"
                              : "items"}
                          </span>
                        </div>

                        <div
                          className="
                            rounded-xl

                            border
                            border-[#E7DCC9]

                            divide-y
                            divide-[#E7DCC9]

                            overflow-hidden
                          "
                        >
                          {items.map(
                            (item, index) => {
                              const itemName =
                                getItemName(item);

                              const image =
                                getItemImage(item);

                              const quantity =
                                getItemQuantity(
                                  item
                                );

                              const size =
                                getItemSize(item);

                              const unitPrice =
                                getItemUnitPrice(
                                  item
                                );

                              const itemTotal =
                                getItemTotal(item);

                              return (
                                <div
                                  key={
                                    item.id ||
                                    item.order_item_id ||
                                    `${order.id}-${index}`
                                  }
                                  className="
                                    p-3
                                    sm:p-4

                                    bg-[#FFFDF9]
                                  "
                                >
                                  <div
                                    className="
                                      flex
                                      items-center

                                      gap-3
                                      sm:gap-4
                                    "
                                  >

                                    {/* IMAGE */}

                                    <div
                                      className="
                                        h-16
                                        w-16

                                        sm:h-20
                                        sm:w-20

                                        shrink-0

                                        overflow-hidden

                                        rounded-lg

                                        bg-[#F0E8DD]
                                      "
                                    >
                                      <img
                                        src={image}
                                        alt={itemName}
                                        className="
                                          h-full
                                          w-full

                                          object-cover
                                        "
                                        loading="lazy"
                                      />
                                    </div>

                                    {/* INFO */}

                                    <div
                                      className="
                                        min-w-0
                                        flex-1
                                      "
                                    >
                                      <p
                                        className="
                                          text-sm
                                          sm:text-base

                                          font-semibold

                                          leading-snug

                                          text-[#332A24]

                                          line-clamp-2
                                        "
                                      >
                                        {itemName}
                                      </p>

                                      <div
                                        className="
                                          mt-1

                                          flex
                                          flex-wrap

                                          items-center

                                          gap-x-3
                                          gap-y-1

                                          text-[10px]
                                          sm:text-xs

                                          text-[#76695D]
                                        "
                                      >
                                        {size && (
                                          <span>
                                            Size:{" "}
                                            {size}
                                          </span>
                                        )}

                                        <span>
                                          Qty:{" "}
                                          {quantity}
                                        </span>

                                        {unitPrice !==
                                          null && (
                                          <span>
                                            {formatMoney(
                                              unitPrice
                                            )}{" "}
                                            each
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* TOTAL */}

                                    <div
                                      className="
                                        shrink-0

                                        text-right
                                      "
                                    >
                                      <p
                                        className="
                                          text-sm
                                          sm:text-base

                                          font-bold

                                          text-[#0F766E]
                                        "
                                      >
                                        {itemTotal !==
                                        null
                                          ? formatMoney(
                                              itemTotal
                                            )
                                          : "—"}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    )}

                    {/* =================================================
                        DELIVERY / PAYMENT / SUMMARY
                    ================================================= */}

                    <div
                      className="
                        mt-6

                        grid

                        grid-cols-1

                        md:grid-cols-3

                        gap-4
                      "
                    >

                      {/* DELIVERY */}

                      <div
                        className="
                          rounded-xl

                          border
                          border-[#E7DCC9]

                          bg-[#FBF8F1]

                          p-4
                        "
                      >
                        <div
                          className="
                            flex
                            items-center
                            gap-2

                            mb-3
                          "
                        >
                          <FaMapMarkerAlt
                            className="
                              text-[#0F766E]
                            "
                          />

                          <h3
                            className="
                              text-xs
                              sm:text-sm

                              font-semibold

                              text-[#332A24]
                            "
                          >
                            Delivery
                          </h3>
                        </div>

                        {shippingAddress ? (
                          <p
                            className="
                              text-xs
                              sm:text-sm

                              leading-relaxed

                              text-[#76695D]
                            "
                          >
                            {shippingAddress}
                          </p>
                        ) : (
                          <p
                            className="
                              text-xs

                              text-[#9A8E82]
                            "
                          >
                            Shipping information will
                            appear here.
                          </p>
                        )}

                        {estimatedDelivery && (
                          <div
                            className="
                              mt-3

                              flex
                              items-center

                              gap-2

                              text-xs

                              text-[#46736C]
                            "
                          >
                            <FaCalendarAlt />

                            <span>
                              Estimated:{" "}
                              {formatShortDate(
                                estimatedDelivery
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* PAYMENT */}

                      <div
                        className="
                          rounded-xl

                          border
                          border-[#E7DCC9]

                          bg-[#FBF8F1]

                          p-4
                        "
                      >
                        <div
                          className="
                            flex
                            items-center
                            gap-2

                            mb-3
                          "
                        >
                          <FaCreditCard
                            className="
                              text-[#0F766E]
                            "
                          />

                          <h3
                            className="
                              text-xs
                              sm:text-sm

                              font-semibold

                              text-[#332A24]
                            "
                          >
                            Payment
                          </h3>
                        </div>

                        <p
                          className="
                            text-xs
                            sm:text-sm

                            text-[#76695D]
                          "
                        >
                          {paymentMethod ||
                            "Payment details available in order details."}
                        </p>

                        {order.paid_at && (
                          <p
                            className="
                              mt-2

                              text-[10px]
                              sm:text-xs

                              text-[#8A8172]
                            "
                          >
                            Paid on{" "}
                            {formatShortDate(
                              order.paid_at
                            )}
                          </p>
                        )}
                      </div>

                      {/* SUMMARY */}

                      <div
                        className="
                          rounded-xl

                          border
                          border-[#E7DCC9]

                          bg-[#FBF8F1]

                          p-4
                        "
                      >
                        <div
                          className="
                            flex
                            items-center
                            gap-2

                            mb-3
                          "
                        >
                          <FaReceipt
                            className="
                              text-[#0F766E]
                            "
                          />

                          <h3
                            className="
                              text-xs
                              sm:text-sm

                              font-semibold

                              text-[#332A24]
                            "
                          >
                            Order summary
                          </h3>
                        </div>

                        <div
                          className="
                            space-y-2

                            text-xs
                            sm:text-sm
                          "
                        >
                          {subtotal !== null && (
                            <div
                              className="
                                flex
                                justify-between

                                text-[#76695D]
                              "
                            >
                              <span>
                                Subtotal
                              </span>

                              <span>
                                {formatMoney(
                                  subtotal
                                )}
                              </span>
                            </div>
                          )}

                          {shipping !== null && (
                            <div
                              className="
                                flex
                                justify-between

                                text-[#76695D]
                              "
                            >
                              <span>
                                Shipping
                              </span>

                              <span>
                                {formatMoney(
                                  shipping
                                )}
                              </span>
                            </div>
                          )}

                          {discount !== null &&
                            Number(discount) > 0 && (
                              <div
                                className="
                                  flex
                                  justify-between

                                  text-[#46736C]
                                "
                              >
                                <span>
                                  Discount
                                </span>

                                <span>
                                  -
                                  {formatMoney(
                                    discount
                                  )}
                                </span>
                              </div>
                            )}

                          {tax !== null && (
                            <div
                              className="
                                flex
                                justify-between

                                text-[#76695D]
                              "
                            >
                              <span>
                                Tax
                              </span>

                              <span>
                                {formatMoney(
                                  tax
                                )}
                              </span>
                            </div>
                          )}

                          <div
                            className="
                              border-t
                              border-[#E7DCC9]

                              pt-2

                              flex
                              justify-between

                              font-bold

                              text-[#332A24]
                            "
                          >
                            <span>
                              Total
                            </span>

                            <span className="text-[#0F766E]">
                              {formatMoney(
                                order.total_amount
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =================================================
                        TRACKING
                    ================================================= */}

                    {(trackingNumber ||
                      carrier ||
                      estimatedDelivery) && (
                      <div
                        className="
                          mt-5

                          rounded-xl

                          border
                          border-[#CFE3DF]

                          bg-[#F1F8F7]

                          p-4
                        "
                      >
                        <div
                          className="
                            flex

                            flex-col
                            sm:flex-row

                            sm:items-center
                            sm:justify-between

                            gap-4
                          "
                        >
                          <div
                            className="
                              flex
                              items-start

                              gap-3
                            "
                          >
                            <div
                              className="
                                flex
                                h-10
                                w-10
                                shrink-0

                                items-center
                                justify-center

                                rounded-full

                                bg-[#E2F0ED]

                                text-[#0F766E]
                              "
                            >
                              <FaTruck />
                            </div>

                            <div>
                              <p
                                className="
                                  text-xs
                                  sm:text-sm

                                  font-semibold

                                  text-[#332A24]
                                "
                              >
                                Shipping & tracking
                              </p>

                              <div
                                className="
                                  mt-1

                                  text-[10px]
                                  sm:text-xs

                                  text-[#5F554D]
                                "
                              >
                                {carrier && (
                                  <span>
                                    Carrier:{" "}
                                    {carrier}
                                  </span>
                                )}

                                {trackingNumber && (
                                  <span className="block mt-1">
                                    Tracking:{" "}
                                    <span className="font-semibold">
                                      {trackingNumber}
                                    </span>
                                  </span>
                                )}

                                {estimatedDelivery && (
                                  <span className="block mt-1 text-[#46736C]">
                                    Estimated delivery:{" "}
                                    {formatShortDate(
                                      estimatedDelivery
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {trackingUrl && (
                            <a
                              href={trackingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="
                                inline-flex

                                items-center
                                justify-center
                                gap-2

                                rounded-lg

                                bg-[#0F766E]

                                px-4
                                py-2.5

                                text-xs
                                sm:text-sm

                                font-semibold

                                text-white

                                transition-all

                                hover:bg-[#115E59]
                                hover:-translate-y-0.5
                              "
                            >
                              Track Package

                              <FaExternalLinkAlt className="text-[10px]" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* =================================================
                        STATUS TIMELINE
                    ================================================= */}

                    {renderTimeline(order)}

                    {/* =================================================
                        ORDER FOOTER
                    ================================================= */}

                    <div
                      className="
                        mt-6
                        pt-5

                        border-t
                        border-[#E7DCC9]

                        flex

                        flex-col
                        sm:flex-row

                        sm:items-center
                        sm:justify-between

                        gap-3
                      "
                    >
                      <div>
                        {order.created_at && (
                          <p
                            className="
                              text-[10px]
                              sm:text-xs

                              text-[#8A8172]
                            "
                          >
                            Order placed{" "}
                            {formatDate(
                              order.created_at
                            )}
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/orders/${order.id}`
                          )
                        }
                        className="
                          inline-flex

                          w-full
                          sm:w-auto

                          items-center
                          justify-center
                          gap-2

                          rounded-xl

                          border-2
                          border-[#0F766E]

                          px-5
                          py-2.5

                          text-xs
                          sm:text-sm

                          font-semibold

                          text-[#0F766E]

                          transition-all

                          hover:bg-[#0F766E]
                          hover:text-white

                          hover:-translate-y-0.5
                        "
                      >
                        <FaEye />

                        View Order Details

                        <FaChevronRight className="text-[10px]" />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}